import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, Award, TrendingUp, Clock, Target, RefreshCw, Users, Package, Box, Search, MapPin, Calendar, Tv, ExternalLink, Info, ScanLine, ChevronDown, ChevronRight, ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const StaffRankings = () => {
  const navigate = useNavigate();
  
  const [period, setPeriod] = useState('daily');
  const [activeCategory, setActiveCategory] = useState('sfg_put_away');
  const [rankings, setRankings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  
  // Pieces processed state
  const [piecesData, setPiecesData] = useState([]);
  const [piecesLoading, setPiecesLoading] = useState(false);
  
  // FG Packing state
  const [packingData, setPackingData] = useState([]);
  const [packingLoading, setPackingLoading] = useState(false);
  
  // FG Pick Slip state
  const [pickSlipData, setPickSlipData] = useState(null);
  const [pickSlipLoading, setPickSlipLoading] = useState(false);
  const [expandedStates, setExpandedStates] = useState({});
  
  // Date filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  
  // Shift filter state
  const [shift, setShift] = useState('all');
  
  // Bin time breakdown dialog state
  const [binBreakdownOpen, setBinBreakdownOpen] = useState(false);
  const [binBreakdownData, setBinBreakdownData] = useState(null);
  const [binBreakdownLoading, setBinBreakdownLoading] = useState(false);

  // 2026-05-28 — Per-slip drill-down state. Clicking the Items Picked
  // or Bins Visited cell opens an inline popover with the design/bin
  // list. Cached per slip_id so re-opening is instant.
  const [slipDetailCache, setSlipDetailCache] = useState({});
  const [slipDetailLoading, setSlipDetailLoading] = useState(null); // slip_id mid-flight
  const [slipDetailOpen, setSlipDetailOpen] = useState(null);       // {slipId, mode: 'items'|'bins'}

  const openSlipDetail = async (slipId, mode) => {
    setSlipDetailOpen({ slipId, mode });
    if (slipDetailCache[slipId]) return;
    setSlipDetailLoading(slipId);
    try {
      const staffId = binBreakdownData?.staff_id;
      const url = `/wms/staff/pick-slip-detail?slip_id=${encodeURIComponent(slipId)}` +
                  (staffId ? `&staff_id=${encodeURIComponent(staffId)}` : '');
      const r = await apiClient.get(url);
      setSlipDetailCache((prev) => ({ ...prev, [slipId]: r.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load slip detail');
      setSlipDetailOpen(null);
    } finally {
      setSlipDetailLoading(null);
    }
  };

  
  useEffect(() => {
    fetchRankings();
    if (activeCategory === 'pieces_processed') {
      fetchPiecesProcessed();
    } else if (activeCategory === 'fg_packing') {
      fetchPackingPerformance();
    } else if (activeCategory === 'fg_pick_slip') {
      fetchPickSlipData();
    }
  }, [period, shift]);
  
  const fetchRankings = async (customDateFrom = null, customDateTo = null) => {
    setLoading(true);
    try {
      let url = `/wms/staff/rankings?period=${period}&shift=${shift}`;
      
      // If custom dates provided, use them
      const useFrom = customDateFrom || dateFrom;
      const useTo = customDateTo || dateTo;
      
      if (useFrom && useTo) {
        url = `/wms/staff/rankings?period=custom&shift=${shift}&date_from=${useFrom}&date_to=${useTo}`;
      }
      
      const response = await apiClient.get(url);
      setRankings(response.data);
    } catch (error) {
      toast.error('Failed to load rankings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApplyDateFilter = () => {
    if (dateFrom && dateTo) {
      fetchRankings(dateFrom, dateTo);
      if (activeCategory === 'pieces_processed') {
        fetchPiecesProcessed(dateFrom, dateTo);
      } else if (activeCategory === 'fg_packing') {
        fetchPackingPerformance(dateFrom, dateTo);
      } else if (activeCategory === 'fg_pick_slip') {
        fetchPickSlipData(dateFrom, dateTo);
      }
    } else {
      toast.error('Please select both from and to dates');
    }
  };
  
  const handleClearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchRankings(null, null);
    if (activeCategory === 'pieces_processed') {
      fetchPiecesProcessed(null, null);
    } else if (activeCategory === 'fg_packing') {
      fetchPackingPerformance(null, null);
    } else if (activeCategory === 'fg_pick_slip') {
      fetchPickSlipData(null, null);
    }
  };
  
  const fetchPiecesProcessed = async (customDateFrom = null, customDateTo = null) => {
    setPiecesLoading(true);
    try {
      let url = `/sections/dispatch/pieces-processed/all-staff-performance?period=${period}&shift=${shift}`;
      
      const useFrom = customDateFrom || dateFrom;
      const useTo = customDateTo || dateTo;
      
      if (useFrom && useTo) {
        url = `/sections/dispatch/pieces-processed/all-staff-performance?period=custom&shift=${shift}&date_from=${useFrom}&date_to=${useTo}`;
      }
      
      const response = await apiClient.get(url);
      setPiecesData(response.data.staff_performance || []);
    } catch (error) {
      toast.error('Failed to load pieces processed data');
      console.error(error);
    } finally {
      setPiecesLoading(false);
    }
  };
  
  const fetchPackingPerformance = async (customDateFrom = null, customDateTo = null) => {
    setPackingLoading(true);
    try {
      let url = `/wms/packing/staff-performance?period=${period}&shift=${shift}`;
      
      const useFrom = customDateFrom || dateFrom;
      const useTo = customDateTo || dateTo;
      
      if (useFrom && useTo) {
        url = `/wms/packing/staff-performance?period=custom&shift=${shift}&date_from=${useFrom}&date_to=${useTo}`;
      }
      
      const response = await apiClient.get(url);
      setPackingData(response.data.staff_performance || []);
    } catch (error) {
      toast.error('Failed to load packing performance data');
      console.error(error);
    } finally {
      setPackingLoading(false);
    }
  };
  
  const fetchPickSlipData = async (customDateFrom = null, customDateTo = null) => {
    setPickSlipLoading(true);
    try {
      let url = `/wms/fg/pick-slips/state-wise-performance?period=${period}&shift=${shift}`;
      
      const useFrom = customDateFrom || dateFrom;
      const useTo = customDateTo || dateTo;
      
      if (useFrom && useTo) {
        url = `/wms/fg/pick-slips/state-wise-performance?period=custom&shift=${shift}&date_from=${useFrom}&date_to=${useTo}`;
      }
      
      const response = await apiClient.get(url);
      setPickSlipData(response.data);
      // Start with all states collapsed
      setExpandedStates({});
    } catch (error) {
      toast.error('Failed to load pick slip data');
      console.error(error);
    } finally {
      setPickSlipLoading(false);
    }
  };
  
  const fetchBinBreakdown = async (staffId, category) => {
    setBinBreakdownLoading(true);
    try {
      let url = `/wms/staff/bin-time-breakdown?staff_id=${staffId}&category=${category}&period=${period}&shift=${shift}`;
      
      if (dateFrom && dateTo) {
        url = `/wms/staff/bin-time-breakdown?staff_id=${staffId}&category=${category}&period=custom&shift=${shift}&date_from=${dateFrom}&date_to=${dateTo}`;
      }
      
      const response = await apiClient.get(url);
      setBinBreakdownData(response.data);
      setBinBreakdownOpen(true);
    } catch (error) {
      toast.error('Failed to load bin time breakdown');
      console.error(error);
    } finally {
      setBinBreakdownLoading(false);
    }
  };
  
  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-gray-500 font-bold">#{rank}</span>;
  };
  
  const getRankBadgeClass = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (rank === 2) return 'bg-gray-100 text-gray-800 border-gray-300';
    if (rank === 3) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-white text-gray-700 border-gray-200';
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const categoryConfig = {
    sfg_put_away: {
      title: 'SFG Put-away',
      icon: MapPin,
      color: 'orange',
      description: 'SFG storage put-away performance'
    },
    sfg_picker: {
      title: 'SFG Picking',
      icon: Package,
      color: 'indigo',
      description: 'Semi-finished goods picking performance'
    },
    pieces_processed: {
      title: 'Pieces Processed',
      icon: ScanLine,
      color: 'teal',
      description: 'Dispatch pieces processing performance'
    },
    put_away: {
      title: 'FG Put-away',
      icon: Target,
      color: 'purple',
      description: 'FG storage put-away performance'
    },
    fg_pick_slip: {
      title: 'FG Pick Slip',
      icon: ClipboardList,
      color: 'rose',
      description: 'FG pick slip qty by state & customer'
    },
    fg_picker: {
      title: 'FG Picking',
      icon: Box,
      color: 'emerald',
      description: 'Finished goods picking performance'
    },
    fg_packing: {
      title: 'FG Packing',
      icon: Package,
      color: 'cyan',
      description: 'FG packing station performance'
    }
  };
  
  const renderRankingCard = (staff, category) => {
    const config = categoryConfig[category];
    
    // For put_away, use display_name if available
    const displayName = category === 'put_away' && staff.display_name 
      ? staff.display_name 
      : staff.staff_name;
    
    return (
      <Card 
        key={staff.staff_id} 
        className={`border-2 ${staff.rank <= 3 ? getRankBadgeClass(staff.rank) : 'border-gray-100'} transition-all hover:shadow-md`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {getRankIcon(staff.rank)}
              <div>
                <p className="font-bold text-lg text-[#1A1F2E]">{staff.staff_name}</p>
                <p className="text-xs text-gray-500">{staff.staff_id}</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${getScoreColor(staff.combined_score)}`}>
                {staff.combined_score}
              </p>
              <p className="text-xs text-gray-500">Combined Score</p>
            </div>
          </div>
          
          {/* Score Breakdown - Main metrics */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="p-2 bg-gray-50 rounded-lg text-center">
              <TrendingUp className="w-4 h-4 mx-auto text-blue-500 mb-1" />
              <p className="text-lg font-bold">{(category === 'put_away' || category === 'sfg_put_away') ? staff.total_items : staff.total_qty}</p>
              <p className="text-xs text-gray-500">Volume</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg text-center">
              <Clock className="w-4 h-4 mx-auto text-purple-500 mb-1" />
              <p className="text-lg font-bold">{staff.speed}</p>
              <p className="text-xs text-gray-500">Items/hr</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg text-center">
              <Target className="w-4 h-4 mx-auto text-green-500 mb-1" />
              <p className="text-lg font-bold">{staff.active_time || 0}</p>
              <p className="text-xs text-gray-500">Active (mins)</p>
              <p className="text-xs text-gray-400">{((staff.active_time || 0) / 60).toFixed(1)} hrs</p>
            </div>
          </div>
          
          {/* Additional metrics for FG Pickers and Put Away - Bins visited and Time in Bins */}
          {(category === 'fg_picker' || category === 'put_away' || category === 'sfg_put_away') && (staff.bins_visited !== undefined || staff.time_in_bins !== undefined) && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="p-2 bg-orange-50 rounded-lg text-center border border-orange-200">
                <MapPin className="w-4 h-4 mx-auto text-orange-500 mb-1" />
                <p className="text-lg font-bold text-orange-700">{staff.bins_visited || 0}</p>
                <p className="text-xs text-orange-600">Bins Visited</p>
              </div>
              <div 
                className="p-2 bg-indigo-50 rounded-lg text-center border border-indigo-200 cursor-pointer hover:bg-indigo-100 transition-colors relative group"
                onClick={() => fetchBinBreakdown(staff.staff_id, category)}
                data-testid={`bin-time-breakdown-${staff.staff_id}`}
              >
                <Info className="w-3 h-3 absolute top-1 right-1 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Clock className="w-4 h-4 mx-auto text-indigo-500 mb-1" />
                <p className="text-lg font-bold text-indigo-700">{staff.time_in_bins || 0}</p>
                <p className="text-xs text-indigo-600">Time in Bins</p>
                <p className="text-xs text-indigo-400">(click for details)</p>
              </div>
            </div>
          )}
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress 
              value={staff.combined_score} 
              className="h-2"
            />
          </div>
        </CardContent>
      </Card>
    );
  };
  
  const renderCategoryTab = (category) => {
    const config = categoryConfig[category];
    const data = rankings?.rankings?.[category] || [];
    const stats = rankings?.summary_stats?.[category] || {};
    const Icon = config.icon;
    
    // Filter data based on search
    const filteredData = searchFilter 
      ? data.filter(s => 
          (s.staff_name || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
          (s.staff_id || '').toLowerCase().includes(searchFilter.toLowerCase())
        )
      : data;
    
    return (
      <div className="space-y-4">
        {/* Summary Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Staff</p>
                  <p className="text-3xl font-bold text-blue-800">{stats.staff_count || 0}</p>
                </div>
                <Users className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Volume</p>
                  <p className="text-3xl font-bold text-green-800">{(stats.total_volume || 0).toLocaleString()}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Avg Speed</p>
                  <p className="text-3xl font-bold text-purple-800">{stats.avg_speed || 0}</p>
                  <p className="text-xs text-purple-500">items/hr</p>
                </div>
                <Clock className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Header with Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 text-${config.color}-500`} />
            <h2 className="text-lg font-bold">{config.title}</h2>
            <Badge variant="outline">{filteredData.length} staff</Badge>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search staff..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
              data-testid="staff-search"
            />
          </div>
        </div>
        
        {/* Top 3 Podium for larger screens */}
        {filteredData.length >= 3 && !searchFilter && (
          <div className="hidden md:grid md:grid-cols-3 gap-4 mb-6">
            {/* 2nd Place */}
            <div className="order-1">
              {filteredData[1] && renderRankingCard(filteredData[1], category)}
            </div>
            {/* 1st Place - Highlighted */}
            <div className="order-0 md:order-1 transform md:scale-105">
              {filteredData[0] && renderRankingCard(filteredData[0], category)}
            </div>
            {/* 3rd Place */}
            <div className="order-2">
              {filteredData[2] && renderRankingCard(filteredData[2], category)}
            </div>
          </div>
        )}
        
        {/* Full List - Skip top 3 if podium is shown, otherwise show all */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredData.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{searchFilter ? 'No matching staff found' : 'No ranking data for this period'}</p>
            </div>
          ) : (
            // If podium is shown (3+ items and no search), skip first 3 in the list
            (filteredData.length >= 3 && !searchFilter 
              ? filteredData.slice(3)  // Skip top 3 when podium is shown
              : filteredData
            ).map((staff) => renderRankingCard(staff, category))
          )}
        </div>
      </div>
    );
  };
  
  const renderPiecesProcessedTab = () => {
    const filteredPieces = searchFilter
      ? piecesData.filter(s =>
          (s.staff_name || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
          (s.staff_id || '').toLowerCase().includes(searchFilter.toLowerCase())
        )
      : piecesData;

    const totalPieces = filteredPieces.reduce((sum, s) => sum + (s.total_pieces || 0), 0);
    const totalBundles = filteredPieces.reduce((sum, s) => sum + (s.total_bundles || 0), 0);

    return (
      <div className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-teal-600 font-medium">Total Staff</p>
                  <p className="text-3xl font-bold text-teal-800">{filteredPieces.length}</p>
                </div>
                <Users className="w-10 h-10 text-teal-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Pieces</p>
                  <p className="text-3xl font-bold text-green-800">{totalPieces.toLocaleString()}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Bundles</p>
                  <p className="text-3xl font-bold text-blue-800">{totalBundles.toLocaleString()}</p>
                </div>
                <Package className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Header with Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-teal-500" />
            <h2 className="text-lg font-bold">Pieces Processed</h2>
            <Badge variant="outline">{filteredPieces.length} staff</Badge>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search staff..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
              data-testid="pieces-staff-search"
            />
          </div>
        </div>

        {/* Table */}
        {piecesLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Rank</TableHead>
                    <TableHead>Staff ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Pieces</TableHead>
                    <TableHead className="text-right">Bundles</TableHead>
                    <TableHead>Last Scan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPieces.map((staff, index) => (
                    <TableRow key={staff.staff_id} data-testid={`pieces-row-${staff.staff_id}`}>
                      <TableCell className="font-bold">
                        {index === 0 ? <Trophy className="w-5 h-5 text-yellow-500 inline" /> :
                         index === 1 ? <Medal className="w-5 h-5 text-gray-400 inline" /> :
                         index === 2 ? <Award className="w-5 h-5 text-amber-600 inline" /> :
                         `#${index + 1}`}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{staff.staff_id}</TableCell>
                      <TableCell className="font-medium">{staff.staff_name}</TableCell>
                      <TableCell className="text-right font-bold text-green-600">{(staff.total_pieces || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{staff.total_bundles || 0}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {staff.last_scan ? new Date(staff.last_scan).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredPieces.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500 py-12">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p>{searchFilter ? 'No matching staff found' : 'No data for this period'}</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };
  
  const renderPackingTab = () => {
    const filteredPacking = searchFilter
      ? packingData.filter(s =>
          (s.staff_name || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
          (s.staff_id || '').toLowerCase().includes(searchFilter.toLowerCase())
        )
      : packingData;

    const totalPieces = filteredPacking.reduce((sum, s) => sum + (s.total_pieces || 0), 0);
    const totalBoxes = filteredPacking.reduce((sum, s) => sum + (s.total_boxes || 0), 0);

    return (
      <div className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-cyan-600 font-medium">Total Staff</p>
                  <p className="text-3xl font-bold text-cyan-800">{filteredPacking.length}</p>
                </div>
                <Users className="w-10 h-10 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Pieces</p>
                  <p className="text-3xl font-bold text-green-800">{totalPieces.toLocaleString()}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Total Boxes</p>
                  <p className="text-3xl font-bold text-orange-800">{totalBoxes.toLocaleString()}</p>
                </div>
                <Box className="w-10 h-10 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Header with Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-500" />
            <h2 className="text-lg font-bold">FG Packing Station</h2>
            <Badge variant="outline">{filteredPacking.length} staff</Badge>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search staff..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
              data-testid="packing-staff-search"
            />
          </div>
        </div>

        {/* Table */}
        {packingLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Rank</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead className="text-right">Pieces</TableHead>
                    <TableHead className="text-right">Boxes</TableHead>
                    <TableHead>Last Packed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPacking.map((staff, index) => (
                    <TableRow key={staff.staff_id} data-testid={`packing-row-${staff.staff_id}`}>
                      <TableCell className="font-bold">
                        {index === 0 ? <Trophy className="w-5 h-5 text-yellow-500 inline" /> :
                         index === 1 ? <Medal className="w-5 h-5 text-gray-400 inline" /> :
                         index === 2 ? <Award className="w-5 h-5 text-amber-600 inline" /> :
                         `#${index + 1}`}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-gray-500">{staff.staff_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">{(staff.total_pieces || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{staff.total_boxes || 0}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {staff.last_packed ? new Date(staff.last_packed).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredPacking.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-12">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p>{searchFilter ? 'No matching staff found' : 'No packing data for this period'}</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };
  
  const toggleState = (stateName) => {
    setExpandedStates(prev => ({ ...prev, [stateName]: !prev[stateName] }));
  };
  
  const renderPickSlipTab = () => {
    const summary = pickSlipData?.summary || {};
    const states = pickSlipData?.states || [];

    // Filter states/customers based on search (case-insensitive)
    const filteredStates = searchFilter
      ? states.map(stateGroup => {
          const q = searchFilter.toLowerCase();
          const matchedCustomers = stateGroup.customers.filter(c =>
            (c.customer || '').toLowerCase().includes(q) ||
            (c.agent || '').toLowerCase().includes(q)
          );
          const stateMatches = stateGroup.state.toLowerCase().includes(q);
          if (stateMatches) return stateGroup;
          if (matchedCustomers.length > 0) {
            return {
              ...stateGroup,
              customers: matchedCustomers,
              customer_count: matchedCustomers.length,
              total_qty: matchedCustomers.reduce((s, c) => s + c.total_qty, 0),
              picked_qty: matchedCustomers.reduce((s, c) => s + c.picked_qty, 0)
            };
          }
          return null;
        }).filter(Boolean)
      : states;

    return (
      <div className="space-y-4">
        {/* 4 Summary Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 md:col-span-1">
            <CardContent className="p-4">
              <p className="text-sm text-purple-600 font-medium">Opening Balance</p>
              <p className="text-3xl font-bold text-purple-800" data-testid="pick-slip-opening-balance">
                {(summary.opening_balance || 0).toLocaleString()}
              </p>
              <p className="text-xs text-purple-500 mt-1">Carried forward</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-600 font-medium">Total Pick Slip Qty</p>
              <p className="text-3xl font-bold text-blue-800" data-testid="pick-slip-total-qty">
                {(summary.total_pick_slip_qty || 0).toLocaleString()}
              </p>
              <p className="text-xs text-blue-500 mt-1">{summary.total_slips || 0} slips</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <p className="text-sm text-green-600 font-medium">Picked Qty</p>
              <p className="text-3xl font-bold text-green-800" data-testid="pick-slip-picked-qty">
                {(summary.picked_qty || 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <p className="text-sm text-amber-600 font-medium">In Progress Qty</p>
              <p className="text-3xl font-bold text-amber-800" data-testid="pick-slip-progress-qty">
                {(summary.in_progress_qty || 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <p className="text-sm text-red-600 font-medium">Pending Qty</p>
              <p className="text-3xl font-bold text-red-800" data-testid="pick-slip-pending-qty">
                {(summary.pending_qty || 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* State-grouped customer list */}
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Search state, customer or agent..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
            data-testid="pick-slip-search"
          />
        </div>
        {pickSlipLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredStates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No pick slip data for this period</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredStates.map((stateGroup) => (
              <Card key={stateGroup.state} className="overflow-hidden" data-testid={`state-group-${stateGroup.state}`}>
                {/* State Header */}
                <button
                  onClick={() => toggleState(stateGroup.state)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  data-testid={`state-toggle-${stateGroup.state}`}
                >
                  <div className="flex items-center gap-3">
                    {expandedStates[stateGroup.state]
                      ? <ChevronDown className="w-5 h-5 text-gray-500" />
                      : <ChevronRight className="w-5 h-5 text-gray-500" />
                    }
                    <MapPin className="w-4 h-4 text-rose-500" />
                    <span className="font-semibold text-gray-800">{stateGroup.state}</span>
                    <Badge variant="outline" className="text-xs">{stateGroup.customer_count} customers</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-800">{stateGroup.total_qty.toLocaleString()}</span>
                      <span className="text-sm text-gray-500 ml-1">qty</span>
                    </div>
                    {stateGroup.picked_qty > 0 && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        {stateGroup.picked_qty.toLocaleString()} picked
                      </Badge>
                    )}
                  </div>
                </button>

                {/* Customer List */}
                {expandedStates[stateGroup.state] && (
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-white">
                          <TableHead>Customer</TableHead>
                          <TableHead>Agent</TableHead>
                          <TableHead className="text-right">Slips</TableHead>
                          <TableHead className="text-right">Total Qty</TableHead>
                          <TableHead className="text-right">Picked Qty</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stateGroup.customers.map((cust) => (
                          <TableRow key={cust.customer} data-testid={`customer-row-${cust.customer}`}>
                            <TableCell className="font-medium">{cust.customer}</TableCell>
                            <TableCell className="text-sm text-gray-500">{cust.agent || '-'}</TableCell>
                            <TableCell className="text-right">{cust.slip_count}</TableCell>
                            <TableCell className="text-right font-bold">{cust.total_qty.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-green-600">{cust.picked_qty.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1F2E] to-[#2D3748] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/dispatch-supervisor')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-400" />
              <div>
                <h1 className="text-xl font-bold font-display">Staff Rankings</h1>
                <p className="text-sm text-[#E8DCC8]">Performance Leaderboard</p>
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchRankings}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/wms/staff-rankings/tv?tab=${activeCategory}&shift=${shift}`, '_blank')}
            className="border-white/20 text-white hover:bg-white/10 ml-2"
            data-testid="tv-mode-btn"
          >
            <Tv className="w-4 h-4 mr-2" />
            TV Display
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </header>
      
      {/* Period Selector */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600">Period:</span>
              <div className="flex gap-2">
                {['daily', 'yesterday', 'weekly', 'monthly'].map((p) => (
                  <Button
                    key={p}
                    variant={period === p && !dateFrom ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setPeriod(p);
                      setDateFrom('');
                      setDateTo('');
                    }}
                    className={period === p && !dateFrom ? 'bg-[#2D5F4F]' : ''}
                    data-testid={`period-${p}`}
                  >
                    {p === 'daily' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'weekly' ? 'This Week' : 'This Month'}
                  </Button>
                ))}
              </div>
              
              {/* Shift Selector */}
              <div className="flex items-center gap-2 ml-4 border-l pl-4">
                <span className="text-sm text-gray-600">Shift:</span>
                <div className="flex gap-1">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'morning', label: '☀️ Morning' },
                    { value: 'night', label: '🌙 Night' }
                  ].map((s) => (
                    <Button
                      key={s.value}
                      variant={shift === s.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setShift(s.value)}
                      className={shift === s.value ? 'bg-[#2D5F4F]' : ''}
                      data-testid={`shift-${s.value}`}
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Date Filter Toggle */}
              <Button
                variant={showDateFilter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowDateFilter(!showDateFilter)}
                className={showDateFilter ? 'bg-[#2D5F4F]' : 'ml-2'}
                data-testid="date-filter-toggle"
              >
                <Calendar className="w-4 h-4 mr-1" />
                Custom Date
              </Button>
            </div>
            
            {/* Score Legend */}
            <div className="hidden md:flex items-center gap-4 text-xs">
              <span className="text-gray-500">Score Formula:</span>
              <Badge variant="outline" className="bg-blue-50">Volume 70%</Badge>
              <Badge variant="outline" className="bg-purple-50">Speed 30%</Badge>
            </div>
          </div>
          
          {/* Shift Info Badge */}
          {shift !== 'all' && (
            <div className="mt-2">
              <Badge className={shift === 'morning' ? 'bg-yellow-100 text-yellow-800' : 'bg-indigo-100 text-indigo-800'}>
                {shift === 'morning' ? '☀️ Morning Shift: 09:30 AM - 08:00 PM IST' : '🌙 Night Shift: 08:01 PM - 09:29 AM IST'}
              </Badge>
            </div>
          )}
          
          {/* Date Filter Panel */}
          {showDateFilter && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">From:</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                  data-testid="date-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">To:</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                  data-testid="date-to"
                />
              </div>
              <Button
                size="sm"
                onClick={handleApplyDateFilter}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="apply-date-filter"
              >
                Apply
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearDateFilter}
                data-testid="clear-date-filter"
              >
                Clear
              </Button>
              {dateFrom && dateTo && (
                <Badge className="bg-blue-100 text-blue-800">
                  {dateFrom} to {dateTo}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <Tabs value={activeCategory} onValueChange={(val) => {
            setActiveCategory(val);
            setSearchFilter('');
            if (val === 'pieces_processed') {
              fetchPiecesProcessed();
            } else if (val === 'fg_packing') {
              fetchPackingPerformance();
            } else if (val === 'fg_pick_slip') {
              fetchPickSlipData();
            }
          }}>
            <TabsList className="flex w-full mb-6 overflow-x-auto">
              <TabsTrigger 
                value="sfg_put_away" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-sfg-putaway"
              >
                <MapPin className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">SFG Put Away</span>
                <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">{rankings?.totals?.sfg_put_away || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="sfg_picker" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-sfg"
              >
                <Package className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">SFG Picking</span>
                <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">{rankings?.totals?.sfg_picker || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="pieces_processed" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-pieces-processed"
              >
                <ScanLine className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">Pieces Processed</span>
                {piecesData.length > 0 && (
                  <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">
                    {piecesData.reduce((sum, s) => sum + (s.total_pieces || 0), 0).toLocaleString()}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="put_away" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-putaway"
              >
                <Target className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">FG Put Away</span>
                <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">{rankings?.totals?.put_away || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="fg_pick_slip" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-fg-pick-slip"
              >
                <ClipboardList className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">FG Pick Slip</span>
                {pickSlipData?.summary && (
                  <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">
                    {pickSlipData.summary.total_pick_slip_qty?.toLocaleString()}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="fg_picker" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-fg"
              >
                <Box className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">FG Picking</span>
                <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">{rankings?.totals?.fg_picker || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="fg_packing" 
                className="flex items-center gap-1 text-xs md:text-sm flex-1 min-w-0"
                data-testid="tab-fg-packing"
              >
                <Package className="w-4 h-4 hidden md:block flex-shrink-0" />
                <span className="truncate">FG Packing</span>
                {packingData.length > 0 && (
                  <Badge variant="secondary" className="ml-1 hidden lg:inline-flex">
                    {packingData.reduce((sum, s) => sum + (s.total_pieces || 0), 0).toLocaleString()}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="sfg_put_away">
              {renderCategoryTab('sfg_put_away')}
            </TabsContent>
            
            <TabsContent value="sfg_picker">
              {renderCategoryTab('sfg_picker')}
            </TabsContent>
            
            <TabsContent value="pieces_processed">
              {renderPiecesProcessedTab()}
            </TabsContent>
            
            <TabsContent value="put_away">
              {renderCategoryTab('put_away')}
            </TabsContent>
            
            <TabsContent value="fg_pick_slip">
              {renderPickSlipTab()}
            </TabsContent>
            
            <TabsContent value="fg_picker">
              {renderCategoryTab('fg_picker')}
            </TabsContent>
            
            <TabsContent value="fg_packing">
              {renderPackingTab()}
            </TabsContent>
          </Tabs>
        )}
      </main>
      
      {/* Bin/Slip Time Breakdown Dialog */}
      <Dialog open={binBreakdownOpen} onOpenChange={setBinBreakdownOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              {binBreakdownData?.breakdown_type === 'slip' ? 'Pick Slip Time Breakdown' : 'Bin Time Breakdown'} - {binBreakdownData?.staff_name || binBreakdownData?.staff_id}
            </DialogTitle>
          </DialogHeader>
          
          {binBreakdownLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="ml-2">Loading breakdown...</span>
            </div>
          ) : binBreakdownData ? (
            <div className="flex-1 overflow-auto">
              {/* Summary Tiles */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {binBreakdownData.breakdown_type === 'slip' ? (
                  <>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <Package className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                        <p className="text-2xl font-bold text-blue-700">{binBreakdownData.summary?.total_slips || 0}</p>
                        <p className="text-sm text-blue-600">Pick Slips</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <MapPin className="w-6 h-6 mx-auto text-purple-600 mb-2" />
                        <p className="text-2xl font-bold text-purple-700">{binBreakdownData.summary?.total_bins || 0}</p>
                        <p className="text-sm text-purple-600">Unique Bins</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-indigo-50 border-indigo-200">
                      <CardContent className="p-4 text-center">
                        <Clock className="w-6 h-6 mx-auto text-indigo-600 mb-2" />
                        <p className="text-2xl font-bold text-indigo-700">{binBreakdownData.summary?.total_time_minutes || 0}</p>
                        <p className="text-sm text-indigo-600">Total Time (mins)</p>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <MapPin className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                        <p className="text-2xl font-bold text-blue-700">{binBreakdownData.summary?.total_bins || 0}</p>
                        <p className="text-sm text-blue-600">Total Bins</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <Target className="w-6 h-6 mx-auto text-purple-600 mb-2" />
                        <p className="text-2xl font-bold text-purple-700">{binBreakdownData.summary?.total_visits || 0}</p>
                        <p className="text-sm text-purple-600">Total Visits</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-indigo-50 border-indigo-200">
                      <CardContent className="p-4 text-center">
                        <Clock className="w-6 h-6 mx-auto text-indigo-600 mb-2" />
                        <p className="text-2xl font-bold text-indigo-700">{binBreakdownData.summary?.total_time_minutes || 0}</p>
                        <p className="text-sm text-indigo-600">Total Time (mins)</p>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
              
              {/* Detailed Table - Different for slip vs bin breakdown */}
              {binBreakdownData.breakdown_type === 'slip' ? (
                /* Slip-based breakdown table for FG Pickers */
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Pick Slip ID</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Start Time</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">End Time</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Items Picked</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Bins Visited</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700 bg-indigo-100">Duration (mins)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {binBreakdownData.slips?.map((slip, idx) => (
                        <tr key={slip.slip_id} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="px-3 py-2 font-medium text-gray-900">{slip.slip_id}</td>
                          <td className="px-3 py-2 text-center text-gray-600">
                            <span className="bg-green-100 px-2 py-0.5 rounded text-xs">{slip.start_time}</span>
                          </td>
                          <td className="px-3 py-2 text-center text-gray-600">
                            <span className="bg-red-100 px-2 py-0.5 rounded text-xs">{slip.end_time}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => openSlipDetail(slip.slip_id, 'items')}
                              className="text-indigo-700 underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!slip.items_picked}
                              data-testid={`slip-items-picked-link-${slip.slip_id}`}
                              title="Click to view design names picked"
                            >
                              {slip.items_picked}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => openSlipDetail(slip.slip_id, 'bins')}
                              className="text-indigo-700 underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!slip.bins_visited}
                              data-testid={`slip-bins-visited-link-${slip.slip_id}`}
                              title="Click to view bin numbers visited"
                            >
                              {slip.bins_visited}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-indigo-700 bg-indigo-50">
                            {slip.duration_minutes}
                          </td>
                        </tr>
                      ))}
                      {(!binBreakdownData.slips || binBreakdownData.slips.length === 0) && (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                            No pick slip activity found for this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Bin-based breakdown table for Put-away */
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Bin No</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Visit</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Timestamps</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Scans</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">Time (mins)</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700 bg-indigo-100">Bin Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {binBreakdownData.bins?.map((bin, binIdx) => (
                        bin.visits?.map((visit, visitIdx) => (
                          <tr 
                            key={`${bin.bin_code}-${visit.visit_number}`} 
                            className={`border-t ${binIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                          >
                            {visitIdx === 0 ? (
                              <td 
                                className="px-3 py-2 font-medium text-gray-900" 
                                rowSpan={bin.visits.length}
                              >
                                {bin.bin_code}
                              </td>
                            ) : null}
                            <td className="px-3 py-2 text-gray-600">
                              <Badge variant="outline" className="text-xs">
                                Visit {visit.visit_number}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              <div className="flex flex-wrap gap-1">
                                {visit.timestamps?.slice(0, 5).map((ts, i) => (
                                  <span key={i} className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                                    {ts}
                                  </span>
                                ))}
                                {visit.timestamps?.length > 5 && (
                                  <span className="text-xs text-gray-400">
                                    +{visit.timestamps.length - 5} more
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center text-gray-600">
                              {visit.scan_count}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-700">
                              {visit.time_spent_minutes}
                            </td>
                            {visitIdx === 0 ? (
                              <td 
                                className="px-3 py-2 text-right font-bold text-indigo-700 bg-indigo-50" 
                                rowSpan={bin.visits.length}
                              >
                                {bin.total_time_minutes}
                              </td>
                            ) : null}
                          </tr>
                        ))
                      ))}
                      {(!binBreakdownData.bins || binBreakdownData.bins.length === 0) && (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                            No bin activity found for this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Footer Info */}
              <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded">
                {binBreakdownData.breakdown_type === 'slip' ? (
                  <p><strong>Note:</strong> Time is calculated as (last scan - first scan) for each pick slip. This represents the active working time on each slip.</p>
                ) : (
                  <p><strong>Note:</strong> Visits are grouped by consecutive scans within 30 minutes. Time is calculated as (last scan - first scan) for each visit.</p>
                )}
                <p className="mt-1">Period: {binBreakdownData.period} | Shift: {binBreakdownData.shift}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No data available</div>
          )}
        </DialogContent>
      </Dialog>

      {/* 2026-05-28 — Items/Bins drill-down popup. Lazy-loaded per slip. */}
      <Dialog
        open={!!slipDetailOpen}
        onOpenChange={(o) => { if (!o) setSlipDetailOpen(null); }}
      >
        <DialogContent className="max-w-2xl" data-testid="slip-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {slipDetailOpen?.mode === 'items' ? (
                <><Package className="w-4 h-4 text-indigo-600" /> Designs picked in {slipDetailOpen?.slipId}</>
              ) : (
                <><MapPin className="w-4 h-4 text-purple-600" /> Bins visited in {slipDetailOpen?.slipId}</>
              )}
            </DialogTitle>
          </DialogHeader>
          {slipDetailLoading === slipDetailOpen?.slipId ? (
            <div className="flex items-center justify-center py-12 text-gray-500 gap-2" data-testid="slip-detail-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : (() => {
            const data = slipDetailCache[slipDetailOpen?.slipId];
            if (!data) return null;
            if (slipDetailOpen?.mode === 'items') {
              if (!data.items?.length) {
                return <div className="py-8 text-center text-gray-500">No items picked on this slip.</div>;
              }
              return (
                <div className="max-h-[60vh] overflow-y-auto border rounded-lg" data-testid="slip-detail-items">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Design</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Color · Size</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Barcode</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700 bg-indigo-100">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((r, i) => (
                        <tr key={r.barcode || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {r.design_name}
                            {r.design_code && <span className="ml-2 text-[10px] text-gray-400">({r.design_code})</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {[r.color, r.size].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{r.barcode || '—'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-indigo-700 bg-indigo-50">{r.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 bg-indigo-50">
                        <td colSpan={3} className="px-3 py-2 text-right font-semibold text-gray-700">Total Picks</td>
                        <td className="px-3 py-2 text-right font-bold text-indigo-700">
                          {data.items.reduce((s, r) => s + (r.qty || 0), 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            }
            // Bins mode
            if (!data.bins?.length) {
              return <div className="py-8 text-center text-gray-500">No bins visited on this slip.</div>;
            }
            return (
              <div className="max-h-[60vh] overflow-y-auto border rounded-lg" data-testid="slip-detail-bins">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Bin No</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700 bg-purple-100">Picks from bin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bins.map((r, i) => (
                      <tr key={r.bin_code || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-mono font-medium text-gray-900">{r.bin_code}</td>
                        <td className="px-3 py-2 text-right font-semibold text-purple-700 bg-purple-50">{r.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-purple-50">
                      <td className="px-3 py-2 text-right font-semibold text-gray-700">Total Visits</td>
                      <td className="px-3 py-2 text-right font-bold text-purple-700">
                        {data.bins.reduce((s, r) => s + (r.qty || 0), 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default StaffRankings;
